import { useQuery } from "@tanstack/react-query";
import { Info, Lock } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { FeatureGate } from "~/components/billing/feature-gate";
import { envConfig } from "~/lib/env-validation";
import type { FinancialsByYearResponse, FinancialsSummaryResponse } from "~/types/financials";
import { AssetCompositionChart } from "../financials/asset-composition-chart";
import { BalanceSheetTable } from "../financials/balance-sheet-table";
import { ExportButtons } from "../financials/export-buttons";
import { FinancialKpiCards } from "../financials/financial-kpi-cards";
import { LineItemsTable } from "../financials/line-items-table";
import { ProfitLossTable } from "../financials/profit-loss-table";
import { RatioCards } from "../financials/ratio-cards";
import { RatioTrendChart } from "../financials/ratio-trend-chart";
import { RevenueChart } from "../financials/revenue-chart";

interface FinancialsTabProps {
	summary: FinancialsSummaryResponse;
}

async function fetchByYear(ico: string, year: number): Promise<FinancialsByYearResponse> {
	const res = await fetch(`${envConfig.apiBaseUrl}/companies/${ico}/financials/${year}`);
	if (!res.ok) throw new Error("Nepodařilo se načíst detail roku");
	return res.json();
}

export function FinancialsTab({ summary }: FinancialsTabProps) {
	const [selectedYear, setSelectedYear] = useState<number | null>(null);

	const { ico, years, truncated, viewerTier, availableYears: totalAvailable } = summary;

	const detailQuery = useQuery({
		queryKey: ["financials-year", ico, selectedYear],
		queryFn: () => fetchByYear(ico, selectedYear as number),
		enabled: selectedYear != null,
	});

	if (years.length === 0) {
		return (
			<div className="py-8 text-center text-muted-foreground">
				<p className="text-sm">Finanční data nejsou k dispozici.</p>
			</div>
		);
	}

	const latestYear = years[years.length - 1];
	const availableYears = years.map((y) => y.year);
	const hiddenYears = Math.max(0, totalAvailable - years.length);

	// Detect balance-sheet-only filings (typical for micro entities, which are
	// not required to publish a profit & loss statement). When no year carries
	// any income-statement data, the revenue/profit charts and tables are empty.
	const hasIncomeData = years.some(
		(y) =>
			y.revenue != null ||
			y.netProfit != null ||
			y.operatingRevenue != null ||
			y.operatingProfit != null ||
			y.profitBeforeTax != null ||
			y.ebitda != null,
	);

	return (
		<div className="flex flex-col gap-6 pt-4">
			{/* Only anon/free can actually unlock more history (free 3y, paid 5y).
			    Premium/Pro/Enterprise already get the max 5 years, so showing them an
			    "Upgradovat" upsell to a plan they own is misleading — suppress it. */}
			{truncated && hiddenYears > 0 && (viewerTier === "anon" || viewerTier === "free") && (
				<div className="rounded-lg border border-dashed border-primary-200 bg-primary-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
					<div className="flex items-start gap-3">
						<Lock className="h-4 w-4 text-primary-600 mt-0.5 shrink-0" />
						<div className="flex flex-col">
							<p className="text-sm font-medium text-foreground">
								{viewerTier === "anon"
									? `${hiddenYears < 5 ? "Další" : "Dalších"} ${hiddenYears} ${hiddenYears === 1 ? "rok" : hiddenYears < 5 ? "roky" : "let"} historie zdarma po registraci`
									: `${hiddenYears < 5 ? "Další" : "Dalších"} ${hiddenYears} ${hiddenYears === 1 ? "rok" : hiddenYears < 5 ? "roky" : "let"} historie v plánu Premium`}
							</p>
							<p className="text-xs text-muted-foreground mt-0.5">
								{viewerTier === "anon"
									? "Po registraci uvidíte 3 roky historie. Premium odemkne 5 let."
									: "Premium plán odemkne plnou 5letou historii."}
							</p>
						</div>
					</div>
					<Link
						to={viewerTier === "anon" ? "/login?mode=register" : "/cenik"}
						className="btn btn-sm btn-primary shrink-0"
					>
						{viewerTier === "anon" ? "Zaregistrovat se" : "Upgradovat"}
					</Link>
				</div>
			)}

			{!hasIncomeData && (
				<div className="rounded-lg border border-dashed border-gray-300 bg-muted/40 p-4 flex items-start gap-3">
					<Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
					<div className="flex flex-col">
						<p className="text-sm font-medium text-foreground">
							Společnost zveřejňuje pouze rozvahu
						</p>
						<p className="text-xs text-muted-foreground mt-0.5">
							Tato společnost je mikro účetní jednotka a ze zákona nezveřejňuje výkaz zisku a
							ztráty. K dispozici jsou proto pouze údaje z rozvahy — tržby, zisk a související
							ukazatele nelze zobrazit.
						</p>
					</div>
				</div>
			)}

			{/* Free tier: KPI Cards + Revenue chart + Asset Composition */}
			<FinancialKpiCards years={years} />
			<RevenueChart years={years} />
			<AssetCompositionChart year={latestYear} />

			{/* Premium tier: Ratio analysis */}
			<FeatureGate minPlan="PREMIUM">
				<RatioCards years={years} />
			</FeatureGate>

			<FeatureGate minPlan="PREMIUM">
				<RatioTrendChart years={years} />
			</FeatureGate>

			{/* Free tier: Summary tables */}
			<BalanceSheetTable years={years} />
			{hasIncomeData && <ProfitLossTable years={years} />}

			{/* Premium tier: Detailed line items */}
			<FeatureGate minPlan="PREMIUM">
				<div className="card-surface rounded-xl p-6">
					<div className="flex items-center justify-between mb-4">
						<h3 className="text-lg font-semibold">Detailní výkaz</h3>
						<select
							value={selectedYear ?? ""}
							onChange={(e) => {
								const val = e.target.value;
								setSelectedYear(val ? Number(val) : null);
							}}
							className="select select-bordered select-sm"
						>
							<option value="">Vyberte rok</option>
							{availableYears.map((y) => (
								<option key={y} value={y}>
									{y}
								</option>
							))}
						</select>
					</div>

					{selectedYear == null && (
						<p className="text-sm text-muted-foreground">
							Vyberte rok pro zobrazení detailních položek výkazu.
						</p>
					)}

					{selectedYear != null && detailQuery.status === "pending" && (
						<div className="flex items-center justify-center py-8">
							<span className="loading loading-spinner loading-md" />
						</div>
					)}

					{selectedYear != null && detailQuery.status === "error" && (
						<div className="rounded-lg border border-red-200 bg-red-50 p-4">
							<p className="text-sm text-red-800">
								Nepodařilo se načíst detail roku.{" "}
								<button
									type="button"
									onClick={() => detailQuery.refetch()}
									className="underline font-medium"
								>
									Zkusit znovu
								</button>
							</p>
						</div>
					)}

					{selectedYear != null && detailQuery.status === "success" && (
						<LineItemsTable lineItems={detailQuery.data.lineItems} />
					)}
				</div>
			</FeatureGate>

			{/* Export buttons (self-gated) */}
			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">Export finančních dat</p>
				<ExportButtons ico={ico} />
			</div>
		</div>
	);
}
